import { domainStatusSchema, type PlatformDomain } from '@avrahami/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { platformDomainsQuery } from '../api/platform';
import { CertificateDate, DomainStatusBadge } from '../components/status';
import { Stat } from '../components/Stat';
import { he } from '../i18n/he';
import { DOMAIN_STATUS_ORDER, totalDomains } from '../lib/domains';
import { errorMessage } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cell,
  formatDateTime,
  Loading,
  PageHeader,
  SelectField,
  Table,
} from '@avrahami/ui';

function DomainRow({ domain, now }: { domain: PlatformDomain; now: number }) {
  return (
    <tr
      data-testid={`domain-row-${domain.hostname}`}
      data-slug={domain.tenantSlug}
      data-primary={String(domain.isPrimary)}
    >
      <Cell>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={domain.url}
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            title={he.domains.opensInNewTab}
            className="ltr font-medium text-brand-700 underline"
          >
            {domain.hostname}
          </a>
          {domain.isPrimary ? (
            <span data-testid="domain-primary">
              <Badge tone="blue">{he.tenant.primary}</Badge>
            </span>
          ) : null}
        </div>
      </Cell>
      <Cell>
        <Link
          to="/tenants/$tenantId/domains"
          params={{ tenantId: domain.tenantId }}
          className="font-medium text-brand-700 hover:underline"
        >
          {domain.tenantName}
        </Link>
      </Cell>
      <Cell>
        <DomainStatusBadge domain={domain} />
      </Cell>
      <Cell className="text-xs text-slate-700">
        {domain.reason === null ? (
          he.common.none
        ) : (
          <span data-testid="domain-reason" data-reason={domain.reason}>
            {he.domains.reasons[domain.reason]}
          </span>
        )}
      </Cell>
      <Cell className="whitespace-nowrap text-xs text-slate-600">
        {domain.lastCheckedAt === null
          ? he.domains.neverChecked
          : formatDateTime(domain.lastCheckedAt)}
      </Cell>
      <Cell className="whitespace-nowrap">
        <CertificateDate validUntil={domain.tlsValidUntil} now={now} />
      </Cell>
    </tr>
  );
}

/**
 * Every store's own domains (spec milestone ג): how many stand in each state, which wait for
 * DNS or lost it, and whose certificate is about to end. Read only: a domain is connected on its
 * store's own "domain management" screen, which each store's name here opens.
 */
export function DomainsPage() {
  const { status } = useSearch({ from: '/app/domains' });
  const navigate = useNavigate({ from: '/domains' });
  const list = useQuery(platformDomainsQuery(status));
  return (
    <>
      <PageHeader
        title={he.domains.title}
        actions={
          <Button
            tone="secondary"
            disabled={list.isFetching}
            onClick={() => {
              void list.refetch();
            }}
          >
            {he.domains.refresh}
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        <p className="text-sm text-slate-600">{he.domains.intro}</p>
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <Alert>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{errorMessage(list.error, { FORBIDDEN: he.domains.viewForbidden })}</span>
              <Button
                tone="secondary"
                busy={list.isFetching}
                onClick={() => {
                  void list.refetch();
                }}
              >
                {he.common.retry}
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            <Card title={he.domains.summary}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                <div data-testid="domain-count-total">
                  <Stat label={he.domains.total} value={totalDomains(list.data.counts)} />
                </div>
                {DOMAIN_STATUS_ORDER.map((each) => (
                  <div key={each} data-testid={`domain-count-${each}`}>
                    <Stat label={he.domains.counts[each]} value={list.data.counts[each]} />
                  </div>
                ))}
              </div>
            </Card>
            <Card title={he.domains.list}>
              <div className="mb-4 max-w-sm">
                <SelectField
                  label={he.domains.filter}
                  data-testid="domain-status-filter"
                  value={status ?? ''}
                  onChange={(event) => {
                    const next = domainStatusSchema.safeParse(event.target.value);
                    void navigate({ search: next.success ? { status: next.data } : {} });
                  }}
                >
                  <option value="">{he.domains.allStatuses}</option>
                  {DOMAIN_STATUS_ORDER.map((each) => (
                    <option key={each} value={each}>
                      {he.domains.status[each]}
                    </option>
                  ))}
                </SelectField>
              </div>
              {list.data.items.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="domains-empty">
                  {status === undefined ? he.domains.empty : he.domains.emptyFiltered}
                </p>
              ) : (
                <Table
                  head={[
                    he.domains.hostname,
                    he.domains.store,
                    he.domains.statusHead,
                    he.domains.reason,
                    he.domains.lastChecked,
                    he.domains.certificateUntil,
                  ]}
                >
                  {list.data.items.map((domain) => (
                    <DomainRow key={domain.id} domain={domain} now={list.dataUpdatedAt} />
                  ))}
                </Table>
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}
